import React from "react";
import { Localizer } from "@/main/react/Types";
import { isPlainLeftClick } from "@/main/react/page/search/searchUtils";
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
    PaginationEllipsis,
} from "@/components/ui/pagination";

/**
 * Makes an `aria-disabled` page control LOOK and BEHAVE disabled. Without it the visual and the
 * assistive-tech experience disagree: the link renders at full contrast and takes the click, and the
 * handler silently returns — a dead affordance.
 */
const DISABLED_PAGE_LINK_CLASSES = "aria-disabled:pointer-events-none aria-disabled:opacity-50";

function getVisiblePages(currentPage: number, availablePages: number[]): number[] {
    const uniquePages = Array.from(new Set(availablePages)).sort((left, right) => left - right);
    const currentSet = new Set<number>([1, currentPage - 1, currentPage, currentPage + 1, uniquePages[uniquePages.length - 1]]);

    return uniquePages.filter((page) => currentSet.has(page) && page > 0);
}

export function SearchPagination(props: {
    currentPage: number;
    availablePages: number[];
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    onPageChange: (page: number) => void;
    localizer: Localizer;
    /**
     * A real destination for a page number (`DISCOVERY-26`).
     *
     * <p>Every control here used to be `href="#"` with a `preventDefault` handler, while the tab strip
     * one row above is deliberately a real anchor — its own docstring calls that "shareable,
     * back/forward friendly, and can be opened in a new browser tab" and treats it as load-bearing. The
     * result was that Cmd-click, middle-click and "open in new tab" worked on a tab and did nothing on
     * page 2, one row apart, with no reason a reader could see.
     *
     * <p>Optional because this component also serves hubs whose page index is component state rather
     * than URL state (`/tag/*`): there is no URL to link to there, and inventing one that does not
     * survive a reload would be worse than the `#`. Plain left-click is still intercepted for the soft
     * navigation in both cases, so nothing changes for a normal click.
     */
    buildPageHref?: (page: number) => string;
}) {
    const { currentPage, availablePages, hasNextPage, hasPreviousPage, onPageChange, localizer, buildPageHref } = props;
    const visiblePages = getVisiblePages(currentPage, availablePages);

    // A page control's href, and the click handler that goes with it. Kept together so the two can
    // never disagree about which page they mean.
    const hrefFor = (page: number): string => buildPageHref?.(page) ?? "#";
    const handleActivate = (event: React.MouseEvent, page: number, enabled: boolean) => {
        // A modified or middle click is the browser's, not ours — that is the whole point of the href.
        if (buildPageHref && !isPlainLeftClick(event)) {
            return;
        }
        event.preventDefault();
        if (enabled) {
            onPageChange(page);
        }
    };

    if (availablePages.length <= 1 && !hasNextPage) {
        return null;
    }

    return (
        <Pagination>
            <PaginationContent>
                <PaginationItem>
                    <PaginationPrevious
                        // A disabled control keeps `#`: there is no page 0 to link to, and emitting one
                        // would hand a middle-click a URL the pager itself refuses to visit.
                        href={hasPreviousPage ? hrefFor(currentPage - 1) : "#"}
                        onClick={(event) => handleActivate(event, currentPage - 1, hasPreviousPage)}
                        aria-disabled={!hasPreviousPage}
                        label={localizer("pagination.previous")}
                        // `aria-disabled` alone told assistive tech the control was dead while it
                        // still rendered at full contrast and accepted clicks that did nothing.
                        className={DISABLED_PAGE_LINK_CLASSES}
                    />
                </PaginationItem>

                {visiblePages.map((page, index) => {
                    const previousPage = visiblePages[index - 1];
                    const shouldShowEllipsis = previousPage != null && page - previousPage > 1;

                    return (
                        <React.Fragment key={page}>
                            {shouldShowEllipsis ? (
                                <PaginationItem>
                                    <PaginationEllipsis />
                                </PaginationItem>
                            ) : null}
                            <PaginationItem>
                                <PaginationLink
                                    href={hrefFor(page)}
                                    isActive={page === currentPage}
                                    onClick={(event) => handleActivate(event, page, true)}
                                >
                                    {page}
                                </PaginationLink>
                            </PaginationItem>
                        </React.Fragment>
                    );
                })}

                {hasNextPage && !visiblePages.includes(currentPage + 1) ? (
                    <PaginationItem>
                        <PaginationEllipsis />
                    </PaginationItem>
                ) : null}

                <PaginationItem>
                    <PaginationNext
                        href={hasNextPage ? hrefFor(currentPage + 1) : "#"}
                        onClick={(event) => handleActivate(event, currentPage + 1, hasNextPage)}
                        aria-disabled={!hasNextPage}
                        label={localizer("pagination.next")}
                        className={DISABLED_PAGE_LINK_CLASSES}
                    />
                </PaginationItem>
            </PaginationContent>
        </Pagination>
    );
}

export default SearchPagination;
